import { useState, useEffect, type FormEvent } from 'react';
import {
  useTelegramSettings,
  type TelegramSettingsInput,
} from '@/hooks/useTelegramSettings';

interface FormState {
  bot_token: string;
  chat_id: string;
  notify_booking: boolean;
  notify_discount_expiry: boolean;
  expiry_days_threshold: string;
}

export default function TelegramManager() {
  const { settings, loading, error, saveSettings, sendTestMessage } = useTelegramSettings();
  const [form, setForm] = useState<FormState>({
    bot_token: '',
    chat_id: '',
    notify_booking: true,
    notify_discount_expiry: true,
    expiry_days_threshold: '3',
  });
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Populate form when settings load (only when not editing yet)
  useEffect(() => {
    if (settings) {
      setForm((prev) => ({
        bot_token: settings.bot_token || prev.bot_token,
        chat_id: settings.chat_id || prev.chat_id,
        notify_booking: settings.notify_booking,
        notify_discount_expiry: settings.notify_discount_expiry,
        expiry_days_threshold: String(settings.expiry_days_threshold ?? 3),
      }));
    }
  }, [settings]);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    window.setTimeout(() => setToast(null), 4000);
  };

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    const botToken = form.bot_token.trim();
    const chatId = form.chat_id.trim();
    const threshold = Number(form.expiry_days_threshold);

    if (!botToken) {
      setFormError('Please enter your bot token.');
      return;
    }
    if (!chatId) {
      setFormError('Please enter your chat ID.');
      return;
    }
    if (Number.isNaN(threshold) || threshold <= 0 || !Number.isInteger(threshold)) {
      setFormError('Expiry warning days must be a positive whole number.');
      return;
    }

    setSaving(true);
    const input: TelegramSettingsInput = {
      bot_token: botToken,
      chat_id: chatId,
      notify_booking: form.notify_booking,
      notify_discount_expiry: form.notify_discount_expiry,
      expiry_days_threshold: threshold,
    };
    const result = await saveSettings(input);
    setSaving(false);

    if (result.error) {
      setFormError(result.error);
      return;
    }
    showToast('success', 'Telegram settings saved.');
  };

  const handleTest = async () => {
    setFormError(null);
    const botToken = form.bot_token.trim();
    const chatId = form.chat_id.trim();

    if (!botToken || !chatId) {
      setFormError('Save your bot token and chat ID first, then send a test message.');
      return;
    }

    setTesting(true);
    const result = await sendTestMessage();
    setTesting(false);

    if (result.error) {
      setFormError(result.error);
      return;
    }
    showToast('success', 'Test message sent! Check your Telegram.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3 text-secondary-500">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          <span className="text-sm">Loading Telegram settings...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">Something went wrong</h3>
        <p className="mt-1 text-sm text-secondary-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-bold text-secondary-900">Telegram notifications</h2>
          <p className="text-sm text-secondary-500 mt-0.5">
            Get real-time alerts for new bookings and expiring discount codes, straight to your phone.
          </p>
        </div>
        <button
          onClick={handleTest}
          disabled={testing}
          className="inline-flex items-center justify-center gap-2 bg-secondary-900 hover:bg-secondary-800 disabled:opacity-60 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className={`${testing ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-2-line'} text-base`} />
          {testing ? 'Sending...' : 'Send test message'}
        </button>
      </div>

      {toast && (
        <div
          className={`flex items-center gap-2 text-sm rounded-md px-4 py-3 ${
            toast.type === 'success'
              ? 'text-primary-700 bg-primary-50 border border-primary-200'
              : 'text-red-600 bg-red-50 border border-red-100'
          }`}
        >
          <i
            className={`${
              toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            } text-base`}
          />
          {toast.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <form
          onSubmit={handleSave}
          className="bg-white rounded-lg border border-secondary-200 p-5 md:p-6"
        >
          <div className="flex items-center gap-2 mb-5">
            <div className="w-10 h-10 rounded-full bg-secondary-900 text-white flex items-center justify-center shrink-0">
              <i className="ri-telegram-line text-lg" />
            </div>
            <div>
              <h3 className="font-heading text-base font-bold text-secondary-900">
                Connection settings
              </h3>
              <p className="text-xs text-secondary-400">Bot token + chat ID</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                Bot token <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={form.bot_token}
                  onChange={(e) => setForm({ ...form, bot_token: e.target.value })}
                  placeholder="123456789:AA... your token from BotFather"
                  className="w-full border border-secondary-200 rounded-md pl-3.5 pr-10 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowToken((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-secondary-400 hover:text-secondary-600 transition-colors cursor-pointer"
                  aria-label={showToken ? 'Hide token' : 'Show token'}
                >
                  <i className={`${showToken ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`} />
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                Chat ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.chat_id}
                onChange={(e) => setForm({ ...form, chat_id: e.target.value })}
                placeholder="e.g. 123456789 or @my_channel"
                className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
              />
            </div>
          </div>

          <div className="mt-6 space-y-4 border-t border-secondary-100 pt-5">
            <p className="text-sm font-medium text-secondary-700">Notification types</p>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.notify_booking}
                onChange={(e) => setForm({ ...form, notify_booking: e.target.checked })}
                className="w-4 h-4 rounded accent-primary-500 cursor-pointer mt-0.5 shrink-0"
              />
              <span className="text-sm text-secondary-700 leading-snug">
                <span className="font-semibold">New booking</span>
                <span className="block text-xs text-secondary-400">
                  Alert me the moment a traveler submits a booking.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.notify_discount_expiry}
                onChange={(e) => setForm({ ...form, notify_discount_expiry: e.target.checked })}
                className="w-4 h-4 rounded accent-primary-500 cursor-pointer mt-0.5 shrink-0"
              />
              <span className="text-sm text-secondary-700 leading-snug">
                <span className="font-semibold">Discount code expiring</span>
                <span className="block text-xs text-secondary-400">
                  Warn me when an active discount code is about to expire.
                </span>
              </span>
            </label>

            <div>
              <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                Warn me when a code expires within (days)
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={form.expiry_days_threshold}
                onChange={(e) => setForm({ ...form, expiry_days_threshold: e.target.value })}
                className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
              />
              <p className="mt-1 text-[11px] text-secondary-400">
                Checked automatically once a day.
              </p>
            </div>
          </div>

          {formError && (
            <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-100 rounded-md px-3.5 py-2.5">
              <i className="ri-error-warning-line text-red-500 text-sm mt-0.5 shrink-0" />
              <p className="text-sm text-red-600">{formError}</p>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold text-sm px-6 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              {saving ? (
                <i className="ri-loader-4-line animate-spin text-base" />
              ) : (
                <>
                  <i className="ri-save-line text-base" />
                  Save settings
                </>
              )}
            </button>
          </div>
        </form>

        {/* How-to */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-secondary-200 p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                <i className="ri-question-line text-lg" />
              </div>
              <h3 className="font-heading text-base font-bold text-secondary-900">
                How to set it up
              </h3>
            </div>

            <ol className="space-y-4">
              {[
                {
                  step: '1',
                  title: 'Create a bot',
                  text: 'Open Telegram, search for @BotFather, and send /newbot. Follow the steps and copy the token it gives you.',
                },
                {
                  step: '2',
                  title: 'Get your chat ID',
                  text: 'Send any message to your new bot, then open https://api.telegram.org/bot<TOKEN>/getUpdates in your browser to find the chat id. (Or message @userinfobot.)',
                },
                {
                  step: '3',
                  title: 'Paste and test',
                  text: 'Paste the token and chat ID on the left, hit Save settings, then send a test message to confirm it works.',
                },
              ].map((item) => (
                <li key={item.step} className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-secondary-900 text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {item.step}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-secondary-800">{item.title}</p>
                    <p className="text-sm text-secondary-500 leading-relaxed mt-0.5 break-words">{item.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
            <div className="flex items-center gap-2">
              <i className="ri-shield-keyhole-line text-amber-600 text-lg" />
              <p className="text-sm font-semibold text-amber-800">Security note</p>
            </div>
            <p className="mt-2 text-sm text-amber-700 leading-relaxed">
              Your bot token is stored securely and only visible to admins. Anyone with the token
              can control your bot, so keep it private.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}